/**
 * Thin typed web API client for the My Searches list (`web/v2`).
 *
 * Separate from `imports.client.ts` even though that file already has a
 * `listSearches()`: that one calls `GET /api/web/v2/searches`, the import
 * Link-to-Search TYPEAHEAD. This one calls `GET /api/web/v2/searches-list`,
 * the paginated Postgres-mirror list. Different endpoint, different response
 * shape, different feature — colocating them under the imports client would
 * have made the imports page's client the owner of a Client Engagement page.
 *
 * Cookie auth rides through the shared `makeAuthedFetch` lifecycle helper
 * (single-flight 401 → `POST /auth/refresh` → retry-once), the same contract
 * `useMe()` and the imports client use — a list left on bare `fetch` breaks
 * the moment the 1h access-token cookie expires mid-session.
 *
 * Typed end-to-end against `@nu-assist/contracts`, so a backend schema change
 * surfaces at `tsc` time rather than as a runtime `undefined`.
 */

import type {
  SearchesListResponse,
  SearchesListSortKey,
  SearchesListSortOrder,
  SearchLeadsResponse,
  SearchStatusFilter,
} from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

/** Minimal `fetch`-shaped function — `makeAuthedFetch(...)` by default. */
export type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface SearchesClientConfig {
  /** API origin (e.g. `import.meta.env.VITE_API_URL`). */
  apiBase: string;
  /** Defaults to `makeAuthedFetch({ apiBase, onSessionExpired })`. Inject a
   *  plain mock in tests. */
  fetchImpl?: FetchLike;
}

export interface ListSearchesParams {
  /** `me` (default) | `all` | a colleague's numeric Thrive user id. */
  owner?: string;
  status?: SearchStatusFilter;
  q?: string;
  /** Column to order the WHOLE result set by; omitted = newest-created first. */
  sort?: SearchesListSortKey;
  /** Direction for `sort` (the API defaults to `asc`; ignored without `sort`). */
  order?: SearchesListSortOrder;
  page?: number;
  page_size?: number;
}

export interface ListSearchLeadsParams {
  /** Scope the options and their counts to the status filter now on screen. */
  status?: SearchStatusFilter;
}

export class SearchesApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message?: string,
  ) {
    super(message ?? `Searches API request failed (${status}) for ${url}`);
    this.name = "SearchesApiError";
    Object.setPrototypeOf(this, SearchesApiError.prototype);
  }
}

const PREFIX = "/api/web/v2/searches-list";

function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createSearchesClient(cfg: SearchesClientConfig) {
  // Built once, not per request. Safe to construct eagerly at module-eval
  // time because `authedFetch` resolves `globalThis.fetch` at CALL time, so
  // MSW's later `server.listen()` patch is still honored.
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  return {
    /** GET /searches-list — one filtered, paginated page. */
    async listSearches(
      params: ListSearchesParams = {},
    ): Promise<SearchesListResponse> {
      const url = `${cfg.apiBase}${PREFIX}${buildQuery(params)}`;
      const res = await authedFetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        throw new SearchesApiError(res.status, url);
      }
      return (await res.json()) as SearchesListResponse;
    },

    /** GET /searches-list/leads — the owner dropdown's colleague options. */
    async listSearchLeads(
      params: ListSearchLeadsParams = {},
    ): Promise<SearchLeadsResponse> {
      const url = `${cfg.apiBase}${PREFIX}/leads${buildQuery(params)}`;
      const res = await authedFetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        throw new SearchesApiError(res.status, url);
      }
      return (await res.json()) as SearchLeadsResponse;
    },
  };
}

export type SearchesClient = ReturnType<typeof createSearchesClient>;
